import type { FormField, FormSchema } from '@kfolks/types'
import { FormFieldType } from '@kfolks/types'

/**
 * Shared form-schema validator. Consumed by:
 *   - Backend `RequestTypeService.create` / `.update` on save.
 *   - Backend `RequestService.submit` (validates submitted `formData`
 *     against the schema's fields — implemented later).
 *   - Frontend admin form-schema editor for inline feedback (Spec 1
 *     JSON editor + Spec 5 visual builder).
 *
 * The validator enforces structural + referential invariants on the
 * FormSchema authoring shape. Submitted-value validation (required /
 * min / max / pattern) is a separate concern handled at submit time.
 */

export interface ValidationError {
	path: string
	message: string
	severity: 'error' | 'warning'
}

export interface ValidationResult {
	valid: boolean
	errors: ValidationError[]
}

export interface FormSchemaValidationContext {
	knownDataSourceKeys: string[]
}

const VALID_FORM_FIELD_TYPES = new Set<string>(Object.values(FormFieldType))
const VALID_CONDITION_OPERATORS = new Set<string>(['equals', 'not_equals', 'contains', 'not_empty'])

const AUTOCOMPLETE_TYPES = new Set<FormFieldType>([
	FormFieldType.AUTOCOMPLETE,
	FormFieldType.AUTOCOMPLETE_MULTI,
	FormFieldType.USER_PICKER,
	FormFieldType.USER_PICKER_MULTI,
	FormFieldType.PROJECT_PICKER,
	FormFieldType.COUNTRY_PICKER,
])

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validateFieldShape(
	raw: unknown,
	path: string,
	errors: ValidationError[],
	fieldIds: Set<string>,
	seenFieldIds: Set<string>,
): void {
	if (!isRecord(raw)) {
		errors.push({ path, message: 'field must be an object', severity: 'error' })
		return
	}
	const f = raw as Partial<FormField>

	if (typeof f.id !== 'string' || f.id.length === 0) {
		errors.push({
			path: `${path}.id`,
			message: 'field.id is required and must be a non-empty string',
			severity: 'error',
		})
		return
	}
	if (seenFieldIds.has(f.id)) {
		errors.push({ path: `${path}.id`, message: `duplicate field id "${f.id}"`, severity: 'error' })
	}
	seenFieldIds.add(f.id)
	fieldIds.add(f.id)

	if (typeof f.label !== 'string' || f.label.length === 0) {
		errors.push({
			path: `${path}.label`,
			message: 'field.label is required and must be a non-empty string',
			severity: 'error',
		})
	}

	if (typeof f.type !== 'string' || !VALID_FORM_FIELD_TYPES.has(f.type)) {
		errors.push({ path: `${path}.type`, message: `unknown field type "${String(f.type)}"`, severity: 'error' })
	}
}

function validateDataSource(
	f: Partial<FormField>,
	path: string,
	errors: ValidationError[],
	knownSources: Set<string>,
): void {
	if (typeof f.type !== 'string' || !AUTOCOMPLETE_TYPES.has(f.type as FormFieldType)) return
	const hasStaticOptions = Array.isArray(f.options) && f.options.length > 0
	if (!f.dataSource && !hasStaticOptions) {
		errors.push({
			path: `${path}.dataSource`,
			message: `field "${f.id}" of type ${f.type} requires a dataSource (or static options)`,
			severity: 'error',
		})
	} else if (f.dataSource && !knownSources.has(f.dataSource)) {
		errors.push({
			path: `${path}.dataSource`,
			message: `unknown data source "${f.dataSource}" on field "${f.id}"`,
			severity: 'error',
		})
	}
}

function validateConditions(
	f: Partial<FormField>,
	path: string,
	errors: ValidationError[],
	fieldIds: Set<string>,
): void {
	if (!Array.isArray(f.conditions)) return
	for (let c = 0; c < f.conditions.length; c++) {
		const cond = f.conditions[c] as Partial<{ field: string; operator: string }>
		const condPath = `${path}.conditions[${c}]`
		if (typeof cond.field !== 'string' || !fieldIds.has(cond.field)) {
			errors.push({
				path: `${condPath}.field`,
				message: `conditional references nonexistent field "${String(cond.field)}"`,
				severity: 'error',
			})
		}
		if (typeof cond.operator !== 'string' || !VALID_CONDITION_OPERATORS.has(cond.operator)) {
			errors.push({
				path: `${condPath}.operator`,
				message: `unknown operator "${String(cond.operator)}"`,
				severity: 'error',
			})
		}
	}
}

function validateFieldReferences(
	raw: unknown,
	path: string,
	errors: ValidationError[],
	fieldIds: Set<string>,
	knownSources: Set<string>,
): void {
	if (!isRecord(raw)) return
	const f = raw as Partial<FormField>
	validateDataSource(f, path, errors, knownSources)
	validateConditions(f, path, errors, fieldIds)
}

export function validateFormSchema(
	schema: FormSchema | unknown,
	context: FormSchemaValidationContext,
): ValidationResult {
	const errors: ValidationError[] = []

	if (!isRecord(schema)) {
		errors.push({ path: '', message: 'formSchema must be an object with shape { version, fields }', severity: 'error' })
		return { valid: false, errors }
	}

	if (schema.version !== '1.0') {
		errors.push({ path: 'version', message: 'Only version "1.0" is supported in Phase 1', severity: 'error' })
	}

	if (!Array.isArray(schema.fields)) {
		errors.push({ path: 'fields', message: 'fields must be an array', severity: 'error' })
		return { valid: false, errors }
	}

	// Once `Array.isArray(schema.fields)` narrows the runtime type, capture it
	// as `unknown[]` so per-element access types as `unknown` (lint flags `any`
	// otherwise — `schema` came in widened to `Record<string, unknown>`).
	const fields: unknown[] = schema.fields
	const knownSources = new Set(context.knownDataSourceKeys)
	const fieldIds = new Set<string>()
	const seenFieldIds = new Set<string>()

	for (let i = 0; i < fields.length; i++) {
		validateFieldShape(fields[i], `fields[${i}]`, errors, fieldIds, seenFieldIds)
	}

	// Second pass — references (dataSource, conditions). Needs the full fieldIds set.
	for (let i = 0; i < fields.length; i++) {
		validateFieldReferences(fields[i], `fields[${i}]`, errors, fieldIds, knownSources)
	}

	return { valid: errors.length === 0, errors }
}
